import type { WorkspaceViewerDirectory } from '@/lib/workspace-viewer-types';

export function isHiddenWorkspaceDotJsonPath(path: string): boolean {
  const name = path.trim().split('/').filter(Boolean).at(-1) ?? '';
  const normalizedName = name.toLowerCase();
  return normalizedName.startsWith('.') && normalizedName.endsWith('.json');
}

export function collectionPanelPathsForDirectory(
  directory: WorkspaceViewerDirectory
): string[] {
  const paths =
    directory.entity === 'collection'
      ? [directory.path]
      : directory.entries.flatMap((entry) =>
          entry.kind === 'directory' && entry.entity === 'collection'
            ? [entry.path]
            : []
        );

  return Array.from(new Set(paths));
}

export function collectionDefaultWorkspacePanelPath(args: {
  parentPath: string;
  collectionPaths: string[];
  competingEntityPaths: string[];
  collectionListingComplete: boolean;
  competingEntityListingsComplete: boolean;
}): string {
  if (
    !args.collectionListingComplete ||
    !args.competingEntityListingsComplete
  ) {
    return args.parentPath;
  }

  const collectionPaths = Array.from(
    new Set(args.collectionPaths.map((path) => path.replace(/\/+$/, '')))
  );
  const competingEntityPaths = new Set(
    args.competingEntityPaths.map((path) => path.replace(/\/+$/, ''))
  );
  return collectionPaths.length === 1 && competingEntityPaths.size === 0
    ? collectionPaths[0]!
    : args.parentPath;
}

export type ManualWorkspacePanelOpenDecision =
  | { kind: 'wait' }
  | { kind: 'open'; path: string | null };

export function manualWorkspacePanelOpenDecision(args: {
  collectionDiscoveryComplete: boolean;
  collectionPath: string | null;
  fallbackPaths: Array<string | null>;
}): ManualWorkspacePanelOpenDecision {
  if (!args.collectionDiscoveryComplete) return { kind: 'wait' };
  return {
    kind: 'open',
    path:
      args.collectionPath ??
      args.fallbackPaths.find((path): path is string => Boolean(path)) ??
      null,
  };
}

export function manualWorkspacePanelOpenPaths(args: {
  nextPath: string;
  collectionPath: string | null;
  defaultPaths: string[];
  existingPaths: string[];
}): string[] {
  const isCollectionDefault = args.nextPath === args.collectionPath;
  return Array.from(
    new Set([
      args.nextPath,
      ...(isCollectionDefault ? [] : args.defaultPaths),
      ...args.existingPaths,
    ])
  );
}

export function mergeRequiredWorkspacePanelTabs(
  openPaths: string[],
  requiredPaths: string[]
): string[] {
  return Array.from(new Set([...openPaths, ...requiredPaths]));
}

export function insertWorkspacePanelTabAfterActive(
  openPaths: string[],
  nextPath: string,
  activePath: string | null,
  maxTabs = 12
): string[] {
  const dedupedPaths = openPaths.filter((openPath) => openPath !== nextPath);
  const activeIndex = activePath
    ? dedupedPaths.findIndex((openPath) => openPath === activePath)
    : -1;
  const nextPaths = [...dedupedPaths];
  nextPaths.splice(
    activeIndex >= 0 ? activeIndex + 1 : nextPaths.length,
    0,
    nextPath
  );
  if (nextPaths.length <= maxTabs) return nextPaths;

  const insertedIndex = nextPaths.findIndex(
    (openPath) => openPath === nextPath
  );
  return insertedIndex < maxTabs
    ? nextPaths.slice(0, maxTabs)
    : nextPaths.slice(-maxTabs);
}

export function replaceWorkspacePanelTabForHistory(
  openPaths: string[],
  nextPath: string,
  activePath: string | null
): string[] {
  const dedupedPaths = openPaths.filter((openPath) => openPath !== nextPath);
  const activeIndex = activePath
    ? dedupedPaths.findIndex((openPath) => openPath === activePath)
    : -1;

  if (activeIndex >= 0) {
    return dedupedPaths.map((openPath, index) =>
      index === activeIndex ? nextPath : openPath
    );
  }

  if (dedupedPaths.length > 0) {
    return dedupedPaths.map((openPath, index) =>
      index === dedupedPaths.length - 1 ? nextPath : openPath
    );
  }

  return [nextPath];
}

/**
 * One browser tab per thread (#4262): a new browser session, for example
 * after an interaction grant reopens the browser, takes over the existing
 * browser tab in place instead of adding a second "Browser" tab. Returns
 * null when no browser tab is open yet.
 */
export function replaceBrowserSessionTab(
  openPaths: string[],
  nextPath: string,
  isBrowserPath: (path: string) => boolean
): string[] | null {
  if (!isBrowserPath(nextPath)) return null;
  const index = openPaths.findIndex(
    (openPath) => openPath !== nextPath && isBrowserPath(openPath)
  );
  if (index < 0) return null;
  return openPaths.flatMap((openPath, position) => {
    if (position === index) return [nextPath];
    return openPath === nextPath || isBrowserPath(openPath) ? [] : [openPath];
  });
}

export function closeWorkspacePanelTab(
  openPaths: string[],
  pathToClose: string,
  activePath: string | null
): { openPaths: string[]; nextActivePath: string | null } {
  const closedIndex = openPaths.findIndex(
    (openPath) => openPath === pathToClose
  );
  const remainingPaths = openPaths.filter(
    (openPath) => openPath !== pathToClose
  );
  if (activePath !== pathToClose) {
    return { openPaths: remainingPaths, nextActivePath: activePath };
  }

  const adjacentIndex = Math.min(
    Math.max(closedIndex, 0),
    remainingPaths.length - 1
  );
  return {
    openPaths: remainingPaths,
    nextActivePath: remainingPaths[adjacentIndex] ?? null,
  };
}

function looksLikeWorkspaceFileName(segment: string): boolean {
  return /\.[A-Za-z0-9][A-Za-z0-9_-]*$/.test(segment);
}

function workspaceDirectoryPathFor(path: string): string {
  const cleanPath = path.split(/[?#]/, 1)[0]?.trim() ?? '';
  const trimmedPath = cleanPath.replace(/\/+$/, '');
  if (!trimmedPath) return path;

  const parts = trimmedPath.split('/').filter(Boolean);
  const lastPart = parts.at(-1);
  if (!lastPart) return '/';
  if (!looksLikeWorkspaceFileName(lastPart)) return `${trimmedPath}/`;

  const slashIndex = trimmedPath.lastIndexOf('/');
  if (slashIndex < 0) return './';
  return `${trimmedPath.slice(0, slashIndex)}/`;
}

function threadAssetDirectoryPathFor(path: string): string | null {
  const cleanPath = path.split(/[?#]/, 1)[0]?.replace(/\/+$/, '') ?? '';
  const parts = cleanPath.split('/').filter(Boolean);
  const threadAssetsIndex = parts.findIndex(
    (part, index) =>
      (part === 'generations' || part === 'shared') &&
      parts[index + 1] === 'thread-assets'
  );
  if (threadAssetsIndex < 0) return null;

  const baseParts = parts.slice(0, threadAssetsIndex + 2);
  const threadPart = parts[threadAssetsIndex + 2];
  if (!threadPart) return `/${baseParts.join('/')}/`;

  const hasNestedPathAfterThread = parts.length > threadAssetsIndex + 3;
  const threadPartLooksLikeFolder = !looksLikeWorkspaceFileName(threadPart);
  const directoryParts =
    hasNestedPathAfterThread || threadPartLooksLikeFolder
      ? [...baseParts, threadPart]
      : baseParts;
  return `/${directoryParts.join('/')}/`;
}

function projectDirectoryPathFor(path: string): string | null {
  const cleanPath = path.split(/[?#]/, 1)[0]?.replace(/\/+$/, '') ?? '';
  const parts = cleanPath.split('/').filter(Boolean);
  const projectIndex = parts.findIndex((part) => part === 'projects');
  const projectSlug = parts[projectIndex + 1];
  if (projectIndex < 0 || !projectSlug) return null;
  return `/${parts.slice(0, projectIndex + 2).join('/')}/`;
}

export function workspaceFinderPathForOpenTabs(
  paths: string[],
  activePath: string,
  currentThreadAssetFolderPath?: string | null
): string {
  const candidates = [activePath, ...paths].filter(Boolean);
  for (const candidate of candidates) {
    const projectPath = projectDirectoryPathFor(candidate);
    if (projectPath) return projectPath;
  }
  for (const candidate of candidates) {
    const threadAssetPath = threadAssetDirectoryPathFor(candidate);
    if (threadAssetPath) return threadAssetPath;
  }
  if (currentThreadAssetFolderPath) return currentThreadAssetFolderPath;
  return workspaceDirectoryPathFor(candidates[0] ?? activePath);
}
